import { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { userLoginSagas } from '../../../ducks/user'

// Components
import LoginForm from '../../components/Forms/Login'
import SecurityTransition from '../../components/Forms/Login/SecurityTransition'
import Logo from '../../components/UI/Logo'
import { Button } from '../../components/UI/Buttons'
// selector
import { errorSelector } from '../../../ducks/user'
// actions
import { setError, setUser } from 'ducks/user'
import { setAuth } from 'ducks/auth'

import { useTranslation } from 'react-i18next'
import { Container, LoginActionRow, LoginDescription, LoginEyebrow } from './styled'
import { IOS_DEVICE, SESSION_TIMEOUT_MESSAGE_KEY } from 'constants/global.constants'
import axios from 'axios'
import jwtDecode from 'jwt-decode'
import { refreshAccessToken, setAuthenticatedSession } from 'services/login/auth-session'
import type { RootState } from 'lib/redux'

const iOS = IOS_DEVICE.test(navigator.userAgent)

interface LoginFormContainerProps {
  variant?: 'admin' | 'evaluated'
}

type RoleType = 'special_admin' | 'super_admin' | 'platform_admin' | 'super_platform_admin' | 'user'

const LoginFormContainer: FC<LoginFormContainerProps> = ({ variant = 'admin' }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const error = useSelector(errorSelector)
  const isLoading = useSelector((state: RootState) => state.user.loading)

  const isEvaluated = variant === 'evaluated'
  const [ssoRedirecting, setSsoRedirecting] = useState(false)
  const [loginErrorMessage, setLoginErrorMessage] = useState<string | null>(null)

  const handleResize = () => {
    window.scrollTo(0, 0)
  }

  useEffect(() => {
    if (iOS) {
      window.addEventListener('resize', handleResize)

      handleResize()

      return () => {
        window.removeEventListener('resize', handleResize)
      }
    }
  }, [])

  useEffect(() => {
    const parameters = new URLSearchParams(window.location.search)
    const ssoError = parameters.get('sso_error')
    if (ssoError === 'MICROSOFT_ACCOUNT_MISMATCH') {
      setLoginErrorMessage(
        'La cuenta Microsoft seleccionada no coincide con el correo indicado. Vuelva a intentarlo con la cuenta correcta.'
      )
      window.history.replaceState({}, document.title, window.location.pathname)
    } else if (ssoError === 'BLUESITE_USER_NOT_REGISTERED') {
      setLoginErrorMessage(
        'La cuenta existe en Microsoft Entra, pero no está registrada en Bluesite o no tiene permisos para acceder. Contacte con su empresa para solicitar el alta o los permisos correspondientes.'
      )
      window.history.replaceState({}, document.title, window.location.pathname)
    } else if (ssoError === 'BLUESITE_USER_INACTIVE') {
      setLoginErrorMessage(
        'La cuenta existe en Bluesite, pero está inactiva. Contacte con su empresa para solicitar la activación.'
      )
      window.history.replaceState({}, document.title, window.location.pathname)
    }
    if (parameters.get('sso_login') !== '1') return

    const completeSsoLogin = async (): Promise<void> => {
      const session = await refreshAccessToken()
      if (!session) {
        setLoginErrorMessage('No fue posible completar la sesión con Microsoft. Inténtalo nuevamente.')
        return
      }

      const tokenData: {
        email?: string
        companyId?: number
        roleType?: RoleType
        userName?: string
        hasAdminPlatformManagement?: boolean
        hasQuestionnaireManagement?: boolean
      } = jwtDecode(session.token)
      setAuthenticatedSession(session.token, session.csrf_token)
      dispatch(
        setUser({
          token: session.token,
          email: tokenData.email || '',
          companyId: tokenData.companyId || 0,
          userName: tokenData.userName || '',
          hasAdminPlatformManagement: Boolean(tokenData.hasAdminPlatformManagement),
          hasQuestionnaireManagement: Boolean(tokenData.hasQuestionnaireManagement),
        })
      )
      dispatch(setAuth({ role: tokenData.roleType || '', isAuth: true }))
      navigate('/', { replace: true })
    }

    void completeSsoLogin()
  }, [dispatch, navigate])

  const handleLogin = async (values: any): Promise<void> => {
    setLoginErrorMessage(null)
    dispatch(setError(false))
    const email = values.email.toLowerCase()
    if (!isEvaluated) {
      const configuredApiUrl = process.env.REACT_APP_MAIN_API?.replace(/\/$/, '')
      const microsoftStartUrl = configuredApiUrl
        ? `${configuredApiUrl}/auth/login/microsoft/start`
        : '/auth/login/microsoft/start'

      try {
        const response = await axios.post(microsoftStartUrl, { email }, { withCredentials: true })

        if (response.data.status === 'SSO_REQUIRED') {
          setSsoRedirecting(true)
          window.setTimeout(() => window.location.assign(response.data.authorization_url), 150)
          return
        }
      } catch {
        setLoginErrorMessage(
          'No hemos podido comprobar el acceso con Microsoft. Comprueba tu conexión e inténtalo de nuevo.'
        )
        return
      }
    }
    if (!values.password) {
      setLoginErrorMessage('Introduce tu contraseña para acceder a Bluesite.')
      return
    }
    dispatch(userLoginSagas.run({ email, password: values.password }))
  }

  useEffect(() => {
    dispatch(setError(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const timeoutMessage: string | null = sessionStorage.getItem(SESSION_TIMEOUT_MESSAGE_KEY)

    if (timeoutMessage) {
      setLoginErrorMessage(timeoutMessage)
      sessionStorage.removeItem(SESSION_TIMEOUT_MESSAGE_KEY)
    }
  }, [])

  useEffect(() => {
    if (error) {
      if (error.code === '401') {
        setLoginErrorMessage(
          'El correo o la contraseña no coinciden. Revisa ambos datos e inténtalo de nuevo. Si has hecho varios intentos, espera unos minutos antes de volver a probar.'
        )
      } else if (error.code === '421') {
        setLoginErrorMessage(t('global.notifications.loginInactiveUserErrorMessage'))
      } else if (error.code === '420') {
        setLoginErrorMessage(t('global.notifications.loginEmailNotMainErrorMessage'))
      } else if (error.code === '422') {
        setLoginErrorMessage(t('global.notifications.deactivateAccountErrorMessage'))
      } else if (error.code === '423' || error.code === 'AUTH_ACCOUNT_LOCKED') {
        setLoginErrorMessage(
          'Por seguridad, el acceso está temporalmente bloqueado tras varios intentos fallidos. Espera unos minutos y vuelve a intentarlo.'
        )
      } else if (error.code === '429') {
        setLoginErrorMessage(
          'Has superado el límite de intentos permitidos. Espera unos minutos antes de volver a intentarlo.'
        )
      } else if (error.code === 'MFA_EMAIL_DELIVERY_FAILED') {
        setLoginErrorMessage('No hemos podido enviar el código de seguridad. Inténtalo nuevamente.')
      } else if (error.code === 'NETWORK_ERROR') {
        setLoginErrorMessage(
          'No hemos podido conectar con Bluesite. Comprueba tu conexión a Internet e inténtalo de nuevo.'
        )
      } else if (['500', '502', '503', '504'].includes(error.code)) {
        setLoginErrorMessage(
          'El servicio de acceso está temporalmente no disponible. Espera unos instantes y vuelve a intentarlo.'
        )
      } else {
        setLoginErrorMessage(
          'No hemos podido completar el acceso. Revisa tus datos y vuelve a intentarlo.'
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, t])

  const handleRetryLogin = (): void => {
    setLoginErrorMessage(null)
    dispatch(setError(false))
  }

  return (
    <Container>
      <SecurityTransition errorMessage={loginErrorMessage} isLoading={isLoading} onRetry={handleRetryLogin} />
      <Logo width="245px" />
      <LoginEyebrow>{isEvaluated ? 'Acceso para evaluados' : 'Acceso seguro para administradores'}</LoginEyebrow>
      <LoginDescription>
        {isEvaluated
          ? 'Ingresa para contestar tus pruebas en una experiencia más clara, enfocada y fácil de seguir.'
          : 'Ingresa para gestionar talento, procesos, analítica y desarrollo con una experiencia más sólida.'}
      </LoginDescription>
      <LoginForm onSubmit={(e) => handleLogin(e)} ssoRedirecting={ssoRedirecting} />
      {ssoRedirecting && <LoginDescription>Estamos conectando de forma segura con Microsoft Entra.</LoginDescription>}
      <LoginActionRow>
        {isEvaluated ? (
          <Button variant="ghost" size="md" onClick={() => navigate('/login')}>
            Volver al acceso de administración
          </Button>
        ) : (
          <Button
            variant="secondary"
            size="md"
            onClick={() => navigate('/login/evaluated')}
            background="linear-gradient(180deg, #F2F9EC 0%, #E9F7DD 100%)"
            color="#34651A"
            border="1px solid #BEE1A6"
          >
            Voy a contestar pruebas
          </Button>
        )}
      </LoginActionRow>
    </Container>
  )
}

export default LoginFormContainer
